import type { FC } from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import SectionBreak from '@/components/Filter/SectionBreak'
import ctx from '@/context/AppContext'
import { useLogContactMutation } from '@/lib/apollo'
import type {
  SectionName,
  AgentContact,
  SubjectContact as SubjectContactPayload,
} from '@/lib/apollo/mutation/log-contact'

import SubjectContact, { DEFAULT_VALUES } from './SubjectContact'
import type { SubjectContactT } from './SubjectContact'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
`

export interface SubjectDetails {
  contactName: string
  subjectId: string
  phoneNumber: string
}

export interface AgentDetails {
  contactName: string
  phoneNumber: string
}

interface Props {
  customerId: string
  sectionName: SectionName
  applicantDetails: SubjectDetails
  refereeDetails?: SubjectDetails
  agentDetails: AgentDetails

  onClose: () => void
}

const toSubjectContactPayload = (
  subjectId: string,
  subjectContact: SubjectContactT,
): SubjectContactPayload => ({
  subjectId,
  contactType: subjectContact.contactType,
})

const toAgentContactPayload = (subjectContact: SubjectContactT): AgentContact => ({
  contactType: subjectContact.contactType,
})

const ContactSidebar: FC<Props> = ({
  customerId,
  sectionName,
  onClose,
  applicantDetails,
  refereeDetails,
  agentDetails,
}) => {
  const [applicantSubjectContact, setApplicantSubjectContact] =
    useState<SubjectContactT>(DEFAULT_VALUES)
  const [refereeSubjectContact, setRefereeSubjectContact] =
    useState<SubjectContactT>(DEFAULT_VALUES)
  const [agentSubjectContact, setAgentSubjectContact] =
    useState<SubjectContactT>(DEFAULT_VALUES)

  const [logContact, { data, loading, error }] = useLogContactMutation()

  const { personId } = useContext(ctx)

  const handleSubmit = () =>
    logContact({
      variables: {
        customerId,
        userId: personId ?? '',
        sectionName,
        subjectId: applicantDetails.subjectId,
        applicantContact: applicantSubjectContact.callWasPlaced
          ? toSubjectContactPayload(applicantDetails.subjectId, applicantSubjectContact)
          : undefined,
        refereeContact:
          refereeDetails !== undefined && refereeSubjectContact.callWasPlaced
            ? toSubjectContactPayload(refereeDetails.subjectId, refereeSubjectContact)
            : undefined,
        agentContact: agentSubjectContact.callWasPlaced
          ? toAgentContactPayload(agentSubjectContact)
          : undefined,
      },
    }).then(onClose)

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<h1>Log contact</h1>}
      content={
        <ContentContainer>
          <SubjectContact
            contactName={applicantDetails.contactName}
            subjectName="Applicant"
            phoneNumber={applicantDetails.phoneNumber}
            subjectContact={applicantSubjectContact}
            onChangeSubjectContact={setApplicantSubjectContact}
          />
          {refereeDetails !== undefined && (
            <>
              <SectionBreak />
              <SubjectContact
                contactName={refereeDetails.contactName}
                subjectName="Referee"
                phoneNumber={refereeDetails.phoneNumber}
                subjectContact={refereeSubjectContact}
                onChangeSubjectContact={setRefereeSubjectContact}
              />
            </>
          )}
          <>
            <SectionBreak />
            <SubjectContact
              contactName={agentDetails.contactName}
              subjectName="Agent"
              phoneNumber={agentDetails.phoneNumber}
              subjectContact={agentSubjectContact}
              onChangeSubjectContact={setAgentSubjectContact}
            />
          </>
          {error?.message && <div>An unexpected error occurred. {error?.message}</div>}
        </ContentContainer>
      }
      footer={
        <div className="side-by-side-flex-end">
          <AlternateButton onClick={onClose}>Cancel</AlternateButton>
          {!data && (
            <Button
              disabled={
                loading ||
                (!applicantSubjectContact.callWasPlaced &&
                  !refereeSubjectContact.callWasPlaced &&
                  !agentSubjectContact.callWasPlaced)
              }
              onClick={handleSubmit}
            >
              Confirm
            </Button>
          )}
        </div>
      }
    />
  )
}

export default ContactSidebar
